import * as React from "react";
import { ActivityIndicator } from "react-native";
import { useTheme } from "@/lib/styling";
import { Theme } from "@/theme";
import { PressableBox, Text, Box } from "@/components/ui/primitives";

type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
type ButtonSize = "default" | "sm" | "lg" | "icon";

export interface ButtonProps extends React.ComponentProps<typeof PressableBox> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fluid?: boolean;
  children?: React.ReactNode;
  loading?: boolean;
}

const Button = React.forwardRef<any, ButtonProps>(
  (
    {
      variant = "default",
      size = "default",
      fluid = false,
      children,
      disabled,
      loading,
      style,
      ...props
    },
    ref
  ) => {
    const theme = useTheme<Theme>();

    const getVariantStyles = () => {
      switch (variant) {
        case "destructive":
          return {
            backgroundColor: "destructive",
            borderColor: "destructive",
            textColor: "destructiveForeground",
          } as const;
        case "outline":
          return {
            backgroundColor: "surface",
            borderColor: "border",
            textColor: "text",
          } as const;
        case "secondary":
          return {
            backgroundColor: "secondary",
            borderColor: "secondary",
            textColor: "textInverse",
          } as const;
        case "ghost":
          return {
            backgroundColor: "transparent",
            borderColor: "transparent",
            textColor: "text",
          } as const;
        case "link":
          return {
            backgroundColor: "transparent",
            borderColor: "transparent",
            textColor: "primary",
          } as const;
        case "default":
        default:
          return {
            backgroundColor: "primary",
            borderColor: "primary",
            textColor: "white",
          } as const;
      }
    };

    const getSizeStyles = () => {
      switch (size) {
        case "sm":
          return {
            height: 36,
            paddingHorizontal: "s",
            paddingVertical: "xs", // 4
          } as const;
        case "lg":
          return {
            height: 48,
            paddingHorizontal: "l",
            paddingVertical: "m",
          } as const;
        case "icon":
          return {
            height: 44,
            width: 44,
            padding: "none",
            alignItems: "center",
            justifyContent: "center",
          } as const;
        case "default":
        default:
          return {
            height: 44,
            paddingHorizontal: "m",
            paddingVertical: "s",
          } as const;
      }
    };

    const variantStyles = getVariantStyles();
    const sizeStyles = getSizeStyles();
    const isChromeLess = variant === "ghost" || variant === "link";
    const textColor = theme.colors[variantStyles.textColor as keyof Theme['colors']];

    return (
      <PressableBox
        ref={ref}
        accessibilityRole="button"
        flexDirection="row"
        alignItems={"center" as any}
        justifyContent={"center" as any}
        borderRadius="m"
        backgroundColor={variantStyles.backgroundColor}
        borderColor={variantStyles.borderColor}
        borderWidth={isChromeLess ? 0 : 1}
        opacity={disabled || loading ? 0.5 : 1}
        disabled={disabled || loading}
        width={fluid ? '100%' : undefined}
        shadowColor="transparent"
        shadowOpacity={0}
        shadowOffset={{ width: 0, height: 0 }}
        shadowRadius={0}
        elevation={0}
        {...sizeStyles}
        {...props}
        style={style as any}
      >
        {loading && (
          <Box marginRight="s">
            <ActivityIndicator 
              size="small" 
              color={textColor} 
            />
          </Box>
        )}
        {typeof children === "string" ? (
          <Text
            variant="button"
            color={variantStyles.textColor}
            textDecorationLine={variant === "link" ? "underline" : "none"}
          >
            {children}
          </Text>
        ) : (
          children
        )}
      </PressableBox>
    );
  }
);

Button.displayName = "Button";

export { Button };
